import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";
import { API_URL, requestOtp, verifyOtp } from "../lib/api";
import { clearSignupSource, getSignupSource } from "../lib/signupSource";

const googleOAuthEnabled = process.env.REACT_APP_GOOGLE_OAUTH_ENABLED === "true";
const RESEND_SECONDS = 45;

const startGoogleAuth = () => {
  const source = getSignupSource();
  window.location.href = `${API_URL}/auth/google/start${source ? `?source=${encodeURIComponent(source)}` : ""}`;
};

// Turns an axios error into actionable, specific text.
function describeAuthError(err) {
  if (!err?.response) {
    return "Cannot connect to server. Please try again.";
  }
  const { status, data } = err.response;
  const detail = data?.detail;
  if (status === 422 && Array.isArray(detail) && detail.length > 0) {
    const first = detail[0];
    const field = Array.isArray(first.loc) ? first.loc[first.loc.length - 1] : "This field";
    return `${field}: ${first.msg}`;
  }
  if (typeof detail === "string" && detail) {
    return detail;
  }
  return err?.message || `Request failed with status ${status}.`;
}

// Replaces the full-page /login and /register routes as the primary auth
// flow — those routes stay live as a fallback for direct links (see App.js),
// but every CTA on Landing.jsx opens this modal instead. Passwordless: one
// email step, one 6-digit code step — the code step both signs an existing
// user in and creates a new account on first use (see POST /auth/otp/verify).
export default function AuthModal({ open, onClose, onAuthed }) {
  const { setUser } = useAuth();
  const [step, setStep] = useState("email"); // "email" | "otp"
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [resendSeconds, setResendSeconds] = useState(RESEND_SECONDS);
  const otpInputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setStep("email");
      setCode("");
      setError("");
      setBusy(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  // 45s resend countdown, restarted every time a code is (re)sent.
  useEffect(() => {
    if (step !== "otp") return undefined;
    setResendSeconds(RESEND_SECONDS);
    const interval = window.setInterval(() => {
      setResendSeconds((s) => Math.max(0, s - 1));
    }, 1000);
    return () => window.clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  useEffect(() => {
    if (step === "otp") otpInputRef.current?.focus();
  }, [step]);

  if (!open) return null;

  const requestCode = async (e) => {
    e?.preventDefault?.();
    if (busy) return;
    setError("");
    setBusy(true);
    try {
      await requestOtp(email);
      setCode("");
      setStep("otp");
    } catch (err) {
      setError(describeAuthError(err));
    } finally {
      setBusy(false);
    }
  };

  const verifyCode = async (candidate) => {
    if (busy) return;
    setError("");
    setBusy(true);
    try {
      const r = await verifyOtp(email, candidate);
      clearSignupSource();
      localStorage.setItem("gumi_token", r.data.access_token);
      setUser(r.data.user);
      onAuthed?.(r.data.user);
    } catch (err) {
      setError(describeAuthError(err));
      setCode("");
      otpInputRef.current?.focus();
    } finally {
      setBusy(false);
    }
  };

  const onCodeChange = (e) => {
    const digits = e.target.value.replace(/\D/g, "").slice(0, 6);
    setCode(digits);
    if (digits.length === 6) verifyCode(digits);
  };

  const resend = () => {
    if (resendSeconds > 0 || busy) return;
    requestCode();
  };

  return createPortal(
    <div
      role="presentation"
      data-testid="auth-modal-overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        background: "rgba(0,0,0,0.4)",
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={step === "email" ? "Sign in" : "Enter your login code"}
        data-testid="auth-modal"
        style={{
          position: "relative",
          width: "100%",
          maxWidth: 420,
          background: "white",
          borderRadius: 20,
          padding: "40px 36px",
          boxShadow: "0 32px 80px rgba(0,0,0,0.25)",
        }}
      >
        <button
          type="button"
          onClick={onClose}
          data-testid="auth-modal-close"
          aria-label="Close"
          style={{
            position: "absolute",
            top: 16,
            right: 16,
            background: "none",
            border: "none",
            fontSize: 20,
            lineHeight: 1,
            color: "#8a8880",
            cursor: "pointer",
            padding: 4,
          }}
        >
          ×
        </button>

        <p style={{ fontSize: 22, fontWeight: 700, marginBottom: 24, color: "#1a1a18" }}>
          Gu<span style={{ color: "#1D9E75" }}>Mi</span>
        </p>

        {step === "email" ? (
          <>
            <h2 style={{ fontSize: 26, fontWeight: 700, lineHeight: 1.25, marginBottom: 8, color: "#1a1a18" }}>
              Know before you <span style={{ color: "#1D9E75" }}>apply</span>.
            </h2>
            <p style={{ fontSize: 14, color: "#5f5e5a", marginBottom: 28 }}>
              Free to start · No credit card needed
            </p>

            {googleOAuthEnabled && (
              <>
                <button
                  type="button"
                  onClick={startGoogleAuth}
                  data-testid="auth-modal-google"
                  style={googleButtonStyle}
                >
                  <GoogleIcon />
                  Continue with Google
                </button>

                <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "16px 0" }}>
                  <div style={{ flex: 1, height: 1, background: "#e5e5e3" }} />
                  <span style={{ fontSize: 12, color: "#8a8880" }}>or</span>
                  <div style={{ flex: 1, height: 1, background: "#e5e5e3" }} />
                </div>
              </>
            )}

            <form onSubmit={requestCode} data-testid="auth-modal-email-form">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com"
                required
                autoFocus
                data-testid="auth-modal-email"
                style={inputStyle}
              />

              {error && (
                <p style={{ fontSize: 13, color: "#C53030", marginBottom: 12 }} data-testid="auth-modal-error">
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={busy}
                data-testid="auth-modal-submit"
                style={{
                  width: "100%",
                  background: "#1D9E75",
                  color: "white",
                  border: "none",
                  borderRadius: 12,
                  padding: 14,
                  fontSize: 15,
                  fontWeight: 700,
                  cursor: busy ? "default" : "pointer",
                  opacity: busy ? 0.7 : 1,
                }}
              >
                {busy ? "Sending…" : "Email me a login code"}
              </button>
            </form>

            <p style={{ marginTop: 20, fontSize: 11, color: "#8a8880", textAlign: "center" }}>
              By continuing you agree to our{" "}
              <Link to="/terms" onClick={onClose} style={{ color: "#8a8880", textDecoration: "underline" }}>
                Terms of Service
              </Link>{" "}
              and{" "}
              <Link to="/privacy" onClick={onClose} style={{ color: "#8a8880", textDecoration: "underline" }}>
                Privacy Policy
              </Link>
            </p>
          </>
        ) : (
          <>
            <button
              type="button"
              onClick={() => setStep("email")}
              data-testid="auth-modal-back"
              style={{
                background: "none",
                border: "none",
                padding: 0,
                marginBottom: 16,
                color: "#5f5e5a",
                fontSize: 13,
                cursor: "pointer",
              }}
            >
              ← Change email
            </button>

            <p style={{ fontSize: 14, color: "#5f5e5a", marginBottom: 24, lineHeight: 1.5 }}>
              Enter the 6-digit code we sent to <strong style={{ color: "#1a1a18" }}>{email}</strong>
            </p>

            <input
              ref={otpInputRef}
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={code}
              onChange={onCodeChange}
              disabled={busy}
              placeholder="······"
              data-testid="auth-modal-otp"
              style={{
                ...inputStyle,
                textAlign: "center",
                fontSize: 24,
                fontWeight: 700,
                letterSpacing: "0.5em",
                paddingLeft: 8,
                paddingRight: 0,
              }}
            />

            {error && (
              <p style={{ fontSize: 13, color: "#C53030", marginBottom: 12 }} data-testid="auth-modal-error">
                {error}
              </p>
            )}

            <button
              type="button"
              onClick={() => verifyCode(code)}
              disabled={busy || code.length !== 6}
              data-testid="auth-modal-verify"
              style={{
                width: "100%",
                background: "#1D9E75",
                color: "white",
                border: "none",
                borderRadius: 12,
                padding: 14,
                fontSize: 15,
                fontWeight: 700,
                cursor: busy || code.length !== 6 ? "default" : "pointer",
                opacity: busy || code.length !== 6 ? 0.7 : 1,
              }}
            >
              {busy ? "Verifying..." : "Verify & continue"}
            </button>

            <p style={{ marginTop: 16, textAlign: "right", fontSize: 12, color: "#8a8880" }}>
              {resendSeconds > 0 ? (
                `Resend in ${resendSeconds}s`
              ) : (
                <button
                  type="button"
                  onClick={resend}
                  data-testid="auth-modal-resend"
                  style={{ background: "none", border: "none", padding: 0, color: "#1D9E75", fontWeight: 600, fontSize: 12, cursor: "pointer" }}
                >
                  Resend code
                </button>
              )}
            </p>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}

const inputStyle = {
  width: "100%",
  border: "1px solid #e5e5e3",
  borderRadius: 12,
  padding: "14px 16px",
  fontSize: 14,
  color: "#1a1a18",
  marginBottom: 12,
  fontFamily: "Inter, sans-serif",
};

const googleButtonStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 10,
  width: "100%",
  background: "#F0F7FF",
  border: "1px solid #E1EFFE",
  borderRadius: 12,
  padding: 14,
  fontSize: 14,
  fontWeight: 600,
  color: "#1a1a18",
  cursor: "pointer",
  marginBottom: 16,
};

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
    <path
      d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.91c1.71-1.57 2.69-3.89 2.69-6.62z"
      fill="#4285F4"
    />
    <path
      d="M9 18c2.43 0 4.47-.81 5.96-2.18l-2.91-2.26c-.81.54-1.84.86-3.05.86-2.34 0-4.32-1.58-5.03-3.71H.96v2.33A9 9 0 0 0 9 18z"
      fill="#34A853"
    />
    <path
      d="M3.97 10.71A5.41 5.41 0 0 1 3.68 9c0-.59.1-1.17.29-1.71V4.96H.96A9 9 0 0 0 0 9c0 1.45.35 2.82.96 4.04l3.01-2.33z"
      fill="#FBBC05"
    />
    <path
      d="M9 3.58c1.32 0 2.51.45 3.44 1.35l2.58-2.58A8.97 8.97 0 0 0 9 0 9 9 0 0 0 .96 4.96l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58z"
      fill="#EA4335"
    />
  </svg>
);
